import {inject, Injectable} from '@angular/core';
import {Facet, FacetItem, FilterGroupHierarchyNode, HierarchicFilterGroup} from '../core/definitions/search-objects';
import {CommonsService} from '../core/commons.service';
import {SearchContainer} from '../core/definitions/search-container';
import {LoggerService} from '../core/logger.service';
import {ProgressDialogComponent} from '../shared/progress-dialog/progress-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SearchViewFilterService} from "./search-view-filter.service";

@Injectable({
  providedIn: 'root'
})
export class HierarchicFilterGroupService {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);

  setHierarchicFilterGroup(searchContainer: SearchContainer) {
    const previousSelectedNode = searchContainer.filtersFacets.hierarchicFilterGroup?.lastSelectedNode;
    const orig = this.searchViewFilterService.getHierarchicFilterGroupFromSearchView(
      searchContainer.currentPathView.search_view);
    let res: HierarchicFilterGroup;
    if (orig) {
      const progressModal = this.matDialog.open(ProgressDialogComponent, {
        disableClose: true,
        panelClass: 'progress-modal'
      });
      try {
        res = this.commons.copy(orig);
        this.generateHierarchy(searchContainer, res);
        this.generateHierarchyArrays(res);
        this.restoreLastSelectedNode(res, previousSelectedNode);
        progressModal.close();
      } catch (e) {
        this.logger.error('Error generating filter hierarchy: ' + e);
        progressModal.close();
      }
      searchContainer.filtersFacets.hierarchicFilterGroup = res;
    } else if (previousSelectedNode) {
      searchContainer.filtersFacets.hierarchicFilterGroup = { lastSelectedNode: previousSelectedNode } as HierarchicFilterGroup;
    } else {
      searchContainer.filtersFacets.hierarchicFilterGroup = null;
    }
  }

  private generateHierarchy(searchContainer: SearchContainer, hierarchicGroup: HierarchicFilterGroup) {
    hierarchicGroup.children = {};
    const searchRes = searchContainer.searchResult;
    const facet = this.getHierarchicFacet(hierarchicGroup.list_filter_field, searchRes.facets);
    if (!facet) {
      return;
    }
    const isPlaceHierarchy = ['m_path', 'materialized_paths.m_path'].includes(hierarchicGroup.list_filter_field) ;
    for (const item of facet.items) {
      const itemName = this.getCleanItemName(item);
      if (!itemName) {
        continue;
      }
      let splitIds: string[];
      let splitNames = this.getSplitNames(itemName, isPlaceHierarchy);
      if (isPlaceHierarchy) {
        splitIds = item.id.split('/');
        splitIds.shift();
      } else {
        splitIds = splitNames;
      }
      let currentHierarchy = hierarchicGroup;
      let currentHierarchyName = '';
      for (let i = 0; i < splitIds.length; i++) {
        let nodeId= splitIds[i];
        if (!isPlaceHierarchy) {
          nodeId = this.getCleanName(nodeId);
        }
        const existing = currentHierarchy.children[nodeId];
        let splitNamePart = this.getSplitNamePart(splitNames, i).replace('</del>', '');
        currentHierarchyName = this.getCurrentHierarchyName(splitNamePart, currentHierarchyName);
        if (!existing) {
          currentHierarchy.children[nodeId] = this.getNewNodeFromPathInfo(
            splitNamePart, currentHierarchyName);
        }
        currentHierarchy = <HierarchicFilterGroup>currentHierarchy.children[nodeId];
      }
      currentHierarchy.facet = this.beautifyItem(item, isPlaceHierarchy);
      currentHierarchy.facet.selected = false;
    }
    if (isPlaceHierarchy) {
      this.setPlaceCounts(hierarchicGroup);
    }
  }

  private getSplitNames(itemName: string, isPlaceHierarchy: boolean) {
    if (!isPlaceHierarchy) {
      return itemName.split(this.commons.getPathSeparatorSymbol());
    } else {
      // If place hierarchy, the item name may have either the formats:
      // "<Last name-part> (<last name-part place-type>)"
      // "<Last name-part> (<last name-part place-type>) - <fist name part>"
      // "<Last name-part> (<last name-part place-type>) - <fist name part>\<second name-part>"
      // "<Last name-part> (<last name-part place-type>) - <fist name part>\<second name-part>\<third name-part>"
      // etc.
      // Must rearrange so the last name-part is moved to the last element of the resulting array
      if (itemName.includes(') - ')) {
        const splitNames = itemName.split(') - ');
        const lastNamePartWithType = splitNames[0] + ')';
        const firstNamePart = splitNames[1];
        const firstNamePartSplit = firstNamePart.split(this.commons.getPathSeparatorSymbol());
        return [...firstNamePartSplit, lastNamePartWithType];
      } else {
        return [itemName];
      }
    }
  }

  private getSplitNamePart(splitNames: string[], i: number): string {
    let name = this.getSplitName(splitNames, i);
    if (!name.length) {
      name = this.getSplitName(splitNames, i - 1);
      if (!name.length) {
        name = this.getSplitName(splitNames, i - 2);
      }
    }
    return name;
  }

  private getSplitName(splitNames: string[], i: number): string {
    let name = '';
    if (i >= 0 && i < splitNames.length) {
      name = splitNames[i].trim();
    }
    return name;
  }

  private getCurrentHierarchyName(name: string, currentHierarchyName: string): string {
    if (!currentHierarchyName.length) {
      currentHierarchyName = name;
    } else {
      currentHierarchyName += this.commons.getPathSeparatorSymbol() + name;
    }
    return currentHierarchyName
  }

  private getNewNodeFromPathInfo(name: string, currentHierarchyName: string): FilterGroupHierarchyNode {
    const newNode = new HierarchicFilterGroup();
    newNode.name = currentHierarchyName;
    newNode.facet.name = name;
    newNode.facet.shortName = name;
    return newNode;
  }

  // Due to using the single value field m_path as facet for place hierarchy, the facet counts are incorrect
  // Must traverser hierarchy to get correct counts
  private setPlaceCounts(hierarchicGroup: HierarchicFilterGroup) {
    for (const childNode of Object.values(hierarchicGroup.children)) {
      if (childNode.children) {
        childNode.facet.count += this.getChildCount(childNode.children);
      }
    }
  }

  private getChildCount(children: {[name:string]: FilterGroupHierarchyNode}): number {
    let res = 0;
    for (const childId in children) {
      const facet = children[childId].facet;
      res += facet.count;
      const grandChildren = children[childId].children;
      if (grandChildren) {
        const recursiveCount = this.getChildCount(grandChildren);
        res += recursiveCount;
        facet.count += recursiveCount;
      }
    }
    return res;
  }

  private getHierarchicFacet(facetName: string, facets: Facet[]): Facet {
    let res = facets.find(facet => facet.f_name === facetName);
    if (!res) {
      this.logger.warn(`Facet not found: ${facetName}`);
    }
    return res;
  }

  // Remove extra item name information that prevents building of hierarchy from working
  private getCleanItemName(facetItem: FacetItem): string {
    const itemNameIn = facetItem.name;
    if (!itemNameIn) {
      this.logger.warn('Item name is empty', facetItem);
      return facetItem.id;
    }
    return this.getCleanName(itemNameIn);
  }

  // Remove extra item name information that prevents building of hierarchy from working
  private getCleanName(itemNameIn: string): string {
    return itemNameIn.replace('<del>', '').replace('</del>', '').trim();
  }

  private beautifyItem(item: FacetItem, isPlaceHierarchy: boolean): FacetItem {
    let itemName = item.name;
    if (!itemName) {
      item.shortName = item.id;
      return item;
    }
    const startsWithDel = itemName.startsWith('<del>') ? '<del>' : '';
    const itemNameWithoutDel = itemName.replace('<del>', '').replace('</del>', '');
    const splitNames = this.getSplitNames(itemNameWithoutDel, isPlaceHierarchy);
    const shortName = this.getSplitNamePart(splitNames, splitNames.length - 1);
    if (startsWithDel) {
      item.shortName = `<del>${shortName}</del>`;
    } else {
      item.shortName = shortName;
    }

    return item;
  }

  // Generating and using arrays will speed up the GUI for drawing nodes
  private generateHierarchyArrays(filterGroupHierarchyNode: FilterGroupHierarchyNode) {
    filterGroupHierarchyNode.childrenArray = Object.values(filterGroupHierarchyNode.children).sort(this.sortNodes);
    for (const node of filterGroupHierarchyNode.childrenArray) {
      this.generateHierarchyArrays(node);
    }
  }

  private sortNodes(node1: FilterGroupHierarchyNode, node2: FilterGroupHierarchyNode) {
    if (!node1.facet.name || !node2.facet.name) {
      return 0;
    }
    const node1Name = node1.facet.name.toLocaleUpperCase();
    const node2Name = node2.facet.name.toLocaleUpperCase();
    if (node1Name < node2Name) {
      return -1
    } else if (node1Name > node2Name) {
      return 1
    } else {
      return 0;
    }
  }

  private restoreLastSelectedNode(
    group: HierarchicFilterGroup,
    previousSelectedNode: FilterGroupHierarchyNode | undefined
  ): void {
    const selectedId = previousSelectedNode?.facet?.id;
    if (!selectedId) {
      return;
    }
    const found = this.findAndSelectNode(group.childrenArray, selectedId);
    // Keep the slim stored node if the live tree does not contain it yet, so search still filters.
    group.lastSelectedNode = found || previousSelectedNode;
  }

  private findAndSelectNode(
    nodes: FilterGroupHierarchyNode[] | undefined,
    selectedId: string
  ): FilterGroupHierarchyNode | null {
    if (!nodes) {
      return null;
    }
    for (const node of nodes) {
      if (node.facet?.id === selectedId) {
        node.facet.selected = true;
        node.open = true;
        return node;
      }
      const child = this.findAndSelectNode(node.childrenArray, selectedId);
      if (child) {
        node.open = true;
        return child;
      }
    }
    return null;
  }
}
